import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import workspaceApi from '../api/workspaceApi';
import { WorkspaceContextWorkspace, WorkspaceContextBusiness } from '../api/types';
import { bootstrapSessionFromLocal, getStoredToken } from '../utils/authStorage';

interface WorkspaceContextType {
    workspaces: WorkspaceContextWorkspace[];
    activeWorkspaceId: string | null;
    setActiveWorkspaceId: (id: string | null) => void;
    businesses: WorkspaceContextBusiness[];
    activeBusinessId: string | null;
    setActiveBusinessId: (id: string | null) => void;
    activeWorkspaceName: string | null;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);
const ACTIVE_BUSINESS_STORAGE_KEY = 'workspace:activeBusinessId';

const getIsAdminSession = () => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem('user_role') === 'admin';
};

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    bootstrapSessionFromLocal();
    const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
    const [activeBusinessId, setActiveBusinessId] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        return window.localStorage.getItem(ACTIVE_BUSINESS_STORAGE_KEY);
    });

    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey: ['workspaceContext'],
        queryFn: workspaceApi.getWorkspaceContext,
        enabled: !!getStoredToken(),
        retry: false,
    });

    const isAdminSession = getIsAdminSession();
    const derivedData = useMemo(() => {
        const workspaces = data?.data?.workspaces ?? [];
        const map = new Map<string, string>();
        for (const workspace of workspaces) {
            for (const business of workspace.businesses || []) {
                if (business.id) {
                    map.set(business.id, workspace.workspace_id);
                }
            }
        }
        const allowedBusinessIds = new Set(map.keys());
        const fallbackBusinessId =
            data?.data?.active_business_id && allowedBusinessIds.has(data.data.active_business_id)
                ? data.data.active_business_id
                : workspaces[0]?.businesses?.[0]?.id || null;
        const targetBusinessId =
            data?.data
                ? (
                    activeBusinessId && (allowedBusinessIds.has(activeBusinessId) || isAdminSession)
                        ? activeBusinessId
                        : fallbackBusinessId
                )
                : null;
        const targetWorkspaceId =
            (targetBusinessId && map.get(targetBusinessId))
            || activeWorkspaceId
            || data?.data?.active_workspace_id
            || workspaces[0]?.workspace_id
            || null;
        const resolvedWorkspace =
            workspaces.find((workspace) => workspace.workspace_id === targetWorkspaceId)
            || null;

        return {
            workspaces,
            businessWorkspaceMap: map,
            allowedBusinessIds,
            targetBusinessId,
            targetWorkspaceId,
            activeWorkspace: resolvedWorkspace,
            businesses: resolvedWorkspace?.businesses || [],
            activeWorkspaceName: resolvedWorkspace?.workspace_name || null,
        };
    }, [activeBusinessId, activeWorkspaceId, data, isAdminSession]);

    // Set defaults from API response and discard stale business ids that are no longer accessible.
    useEffect(() => {
        if (!data?.data) return;
        if (activeWorkspaceId !== derivedData.targetWorkspaceId) {
            setActiveWorkspaceId(derivedData.targetWorkspaceId);
        }
        if (activeBusinessId !== derivedData.targetBusinessId) {
            setActiveBusinessId(derivedData.targetBusinessId);
        }
    }, [activeBusinessId, activeWorkspaceId, data, derivedData.targetBusinessId, derivedData.targetWorkspaceId]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (activeBusinessId) {
            window.localStorage.setItem(ACTIVE_BUSINESS_STORAGE_KEY, activeBusinessId);
            return;
        }
        window.localStorage.removeItem(ACTIVE_BUSINESS_STORAGE_KEY);
    }, [activeBusinessId]);

    // When workspace changes, reset business to the first one in that workspace
    const handleSetActiveWorkspaceId = (id: string | null) => {
        setActiveWorkspaceId(id);
        const ws = derivedData.workspaces.find(w => w.workspace_id === id);
        setActiveBusinessId(ws?.businesses?.[0]?.id || null);
    };
    const handleSetActiveBusinessId = (id: string | null) => {
        if (id && derivedData.businessWorkspaceMap.has(id)) {
            setActiveWorkspaceId(derivedData.businessWorkspaceMap.get(id) || null);
        }
        setActiveBusinessId(id);
    };

    return (
        <WorkspaceContext.Provider value={{
            workspaces: derivedData.workspaces,
            activeWorkspaceId,
            setActiveWorkspaceId: handleSetActiveWorkspaceId,
            businesses: derivedData.businesses,
            activeBusinessId: derivedData.targetBusinessId,
            setActiveBusinessId: handleSetActiveBusinessId,
            activeWorkspaceName: derivedData.activeWorkspaceName,
            isLoading,
            isFetching,
            isError,
            refetch,
        }}>
            {children}
        </WorkspaceContext.Provider>
    );
};

export const useWorkspace = () => {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
};
